import {
	NoResults,
	useConfirmationDialog
} from "client/Components";
import {
	ReactElement,
	ReadonlyRefObject,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState
} from "react";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	AnnouncementDetailsViewModel,
	AnnouncementPreviewItemModel,
	getAnnouncementDetails,
	getStatistics,
	registerView,
	AnnouncementStatisticsModel,
	getReactionCounts,
	getReactions
} from "client/Api/Hub/Announcements/announcementViewer";
import { HubItemCommentSection } from "../../HubItemCommentSection/HubItemCommentSection";
import {
	AlertManager,
	useAlerts,
	useSynchronizedRef,
	useUnmountedCancellation,
	useUserTimezone
} from "client/Hooks";
import { ContentWithRelevant } from "../../ContentWithRelevant";
import { MediaContentModel } from "../../HubDetailsLayout";
import { useReactionsDialog } from "../../../Reactions/ReactionsDialog/ReactionsDialog";
import {
	ActiveFilter,
	HubDialogContext,
	HubItemIsNewFilter,
	listHubImageUrlWithAttributions,
	ImageUrlWithAttribution
} from "../../common";
import { HubDataSource, HubItemCategoryModel } from "client/Api/Hub/hub";
import { EditDialog } from "../../Management/AnnouncementManagement/EditAnnouncementDialog/EditAnnouncementDialog";
import {
	EMPTY_ARRAY,
	ensureNotNil,
	isNotNil
} from "client/Misc/utils";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import styles from "./Details.module.less";
import {
	getHubImageUrl,
	HubCommentModel,
	HubLinkedDocumentViewModel,
	HubItemMediaKind
} from "client/Api/Hub/common";
import {
	HubCommentOptionsModel,
	HubItemMediaDetailsViewModel,
	HubItemViewLocation
} from "client/Api/Hub/common.details";
import {
	DataSourceSortableState,
	DataSourceSortDirection,
	nativeSortFunction
} from "client/Data/sorting";
import { RelevantAnnouncementsSection } from "./RelevantAnnouncementsSection";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";
import { TagViewModel } from "client/Api/Tags/tags";
import { TimeZone } from "client/DateAndTime/timezones";
import { useImageViewer } from "../../useImageViewer";
import { HttpRequestError } from "client/Api/httpClient";
import { convertTagForDetails } from "../../converters";
import {
	AnnouncementContent,
	AnnouncementContentDataProps,
	AnnouncementContentModeProps,
	AnnouncementContentStatisticsProps
} from "./AnnouncementContent";
import { never } from "client_legacy/ndaw/errors";
import { MobileCommentBoxPreservedSpace } from "../../HubCommentSection/MobileComment/MobileCommentBoxPreservedSpace";
import { HubEvent, publish } from "../../events";
import { useViewersDialog } from "../../ViewersDialog/ViewersDialog";
import { CommentThread } from "../../HubCommentSection/CommentThread";
import { getRelatedCommentThread } from "client/Api/Hub/Announcements/announcementComments";
import { getAnnouncementCommentService } from "../getAnnouncementCommentService";
import { HubCommentSection, HubCommentSectionService } from "../../HubCommentSection/HubCommentSection";

type DetailsModeProps = ConditionalProps<
	{
		selectedCategoryId: number | null;
		onNavigateToItem(id: number): void;
		activeFilter: ActiveFilter | null;
		onActiveFilterChange: (filter: ActiveFilter) => void;
		onBack(): void;
		onCategoryCreated: () => void;
		searchTerm: VersionedValue<string>;
		sorting: DataSourceSortableState<AnnouncementPreviewItemModel>;
		isNewFilter: HubItemIsNewFilter;
	},
	"mode",
	"full",
	"details-only"
>;

export type DetailsProps = {
	announcementId: number;
	getCategory: (categoryId: number | nil) => HubItemCategoryModel | undefined;
	deepLink?: AnnouncementDetailsDeepLink;
	viewLocation: HubItemViewLocation;
	onLoadingError?: (error: unknown) => TryResult<void>;
	onSelectCategory: (categoryId: number) => void;
}
	& DetailsModeProps;

export const Details = (props: DetailsProps): ReactElement => {
	const {
		announcementId,
		getCategory,
		deepLink,
		viewLocation,
		mode,
		onSelectCategory,
		onBack,
		onActiveFilterChange,
		onCategoryCreated,
		activeFilter,
		onNavigateToItem,
		searchTerm,
		selectedCategoryId,
		sorting,
		isNewFilter,
		onLoadingError
	} = props;

	const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>("loading");
	const [announcement, setAnnouncement] = useState<AnnouncementDetailsViewModel | null>(null);
	const [commentOptions, setCommentOptions] = useState<HubCommentOptionsModel | null>(null);
	const [statistics, setStatistics] = useState<AnnouncementStatisticsModel>();
	const [isEditingMobileComment, setIsEditingMobileComment] = useState<boolean>(false);
	const [idToEdit, setIdToEdit] = useState<number | null>(null);

	const [commentsState, setCommentsState] = useState<CommentsState>(
		(): CommentsState => deepLink?.kind === "comment"
			? {
				kind: "comment-thread",
				commentId: deepLink.commentId
			}
			: {
				kind: "all-comments",
				shouldReserveSpace: false
			}
	);

	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const alertManager: AlertManager = useAlerts();
	const userTimezone: TimeZone = useUserTimezone();

	const { setViewSpecificHandlers } = useContext(HubDialogContext);

	const [confirmationDialogElement, openConfirmationDialog] = useConfirmationDialog();
	const [reactionsDialog, openReactionsDialog] = useReactionsDialog();
	const [viewersDialog, openViewersDialog] = useViewersDialog();

	const imageViewerItems = useMemo(
		(): readonly ImageUrlWithAttribution[] => {
			return listHubImageUrlWithAttributions(HubDataSource.Announcement, announcement);
		},
		[announcement]
	);

	const [imageViewerDialog, viewImageHandler] = useImageViewer(imageViewerItems);
	const onLoadingErrorRef: ReadonlyRefObject<((error: unknown) => TryResult<void>) | undefined> = useSynchronizedRef(onLoadingError);
	const viewLocationRef: ReadonlyRefObject<HubItemViewLocation> = useSynchronizedRef(viewLocation);
	const linkedDocuments: readonly HubLinkedDocumentViewModel[] = announcement?.linkedDocuments
		?? EMPTY_ARRAY;

	const registerViewAndFetchStatisticsHandler = useCallback(
		async (announcementId: number, signal: AbortSignal): Promise<void> => {
			try {
				await registerView(
					announcementId,
					{ location: viewLocationRef.current },
					signal
				);
				const statistics: AnnouncementStatisticsModel = await getStatistics(announcementId, signal);

				setStatistics(statistics);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
			}
		},
		[viewLocationRef]
	);

	const reloadStatistics = useCallback(
		async (): Promise<void> => {
			try {
				const statistics: AnnouncementStatisticsModel = await getStatistics(announcementId, globalCancellation);
				setStatistics(statistics);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
			}
		},
		[announcementId, globalCancellation]
	);

	const fetchItem = useCallback(
		async (
			announcementId: number,
			signal: AbortSignal,
			isFirstLoad: boolean = false
		): Promise<void> => {
			setAnnouncement(null);
			setLoadingStatus("loading");

			try {
				const {
					announcement,
					commentOptions
				} = await getAnnouncementDetails(announcementId, signal);

				setAnnouncement(announcement);
				setCommentOptions(commentOptions);
				setLoadingStatus("found");

				if (isFirstLoad) {
					if (announcement.isNew) {
						publish(
							HubEvent.IsNewStatusCleared,
							{
								kind: HubDataSource.Announcement,
								itemId: announcement.announcementId,
								categoryIds: announcement.categoryIds
							}
						);
					}

					registerViewAndFetchStatisticsHandler(announcement.announcementId, signal);
				} else {
					reloadStatistics();
				}
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				let loadingStatus: LoadingStatus = "error";

				if (e instanceof HttpRequestError && e.statusCode === 404) {
					loadingStatus = "not-found";
				}

				setLoadingStatus(loadingStatus);

				const { current: onLoadingError } = onLoadingErrorRef;

				const wasHandled: boolean = onLoadingError?.(e)
					?.wasSuccessful ?? false;

				if (!wasHandled) {
					console.error(e);
					alertManager.showError(localize("_CannotOpenItem"));
				}
			}
		},
		[
			alertManager,
			onLoadingErrorRef,
			registerViewAndFetchStatisticsHandler,
			reloadStatistics
		]
	);

	useEffect(
		() => {
			const cancellation = new AbortController();
			fetchItem(announcementId, cancellation.signal, true);

			return () => cancellation.abort();
		},
		[
			alertManager,
			announcementId,
			fetchItem
		]
	);

	const allCommentViewHandler = useCallback(
		(): void => {
			setCommentsState({
				kind: "all-comments",
				shouldReserveSpace: true
			});
		},
		[]
	);

	const promptUserBeforeQuittingThePage = useCallback(
		async (): Promise<boolean> => {
			const result: TryResult<void> = await openConfirmationDialog({
				title: localize("_AreYouSureYouWantToLeaveThisPage"),
				children: localize("_YouHaveStartedWritingOrEditingAComment"),
				confirmText: localize("_Stay"),
				cancelText: localize("_Leave"),
				trackingId: "discard-announcement-comment-form-confirmation-dialog"
			});

			if (result.wasSuccessful) {
				return true;
			}

			return false;
		},
		[openConfirmationDialog]
	);

	const guardAgainstUnsavedComment = useCallback(
		async (handler: () => void): Promise<void> => {
			if (isEditingMobileComment) {
				const shouldStay: boolean = await promptUserBeforeQuittingThePage();
				if (shouldStay) {
					return;
				}
			}

			handler();
		},
		[isEditingMobileComment, promptUserBeforeQuittingThePage]
	);

	const backHandler = useMemo(
		(): (() => void) | undefined => {
			if (mode !== "full") {
				return undefined;
			}

			return (): void => {
				guardAgainstUnsavedComment(
					() => onBack()
				);
			};
		},
		[
			guardAgainstUnsavedComment,
			mode,
			onBack
		]
	);

	useEffect(
		(): (() => void) => {

			const beforeLeavingDetailsHandler: (() => Promise<boolean>) | undefined = !isEditingMobileComment
				? undefined
				: promptUserBeforeQuittingThePage;

			setViewSpecificHandlers({
				backHandler,
				beforeCloseHandler: beforeLeavingDetailsHandler,
				beforeSwitchingCategoryHandler: beforeLeavingDetailsHandler
			});

			return () => {
				setViewSpecificHandlers(undefined);
			};
		},
		[
			backHandler,
			isEditingMobileComment,
			promptUserBeforeQuittingThePage,
			setViewSpecificHandlers
		]
	);

	const afterSaveHandler = useCallback(
		(): void => {
			fetchItem(announcementId, globalCancellation);
		},
		[
			announcementId,
			globalCancellation,
			fetchItem
		]
	);

	const editHandler = useCallback(
		(): void => {
			setIdToEdit(
				ensureNotNil(announcement?.announcementId)
			);
		},
		[announcement?.announcementId]
	);

	const closeEditDialogHandler = useMemo(
		(): ((wasAnyCategoryCreated: boolean) => void) | undefined => {
			if (mode !== "full") {
				return undefined;
			}

			return (wasAnyCategoryCreated): void => {
				setIdToEdit(null);

				if (wasAnyCategoryCreated) {
					onCategoryCreated();
				}
			};
		},
		[mode, onCategoryCreated]
	);

	const announcementContentDataProps = useMemo(
		(): AnnouncementContentDataProps => {
			if (!announcement) {
				return {
					hasData: false
				};
			}

			const categories: readonly HubItemCategoryModel[] = announcement.categoryIds
				.map((x: number): HubItemCategoryModel | undefined => getCategory(x))
				.filter(isNotNil);

			const sortedCategories: readonly HubItemCategoryModel[] = [...categories].sort(
				(a: HubItemCategoryModel, b: HubItemCategoryModel): number => {
					if (a.id === selectedCategoryId) {
						return -1;
					}

					if (b.id === selectedCategoryId) {
						return 1;
					}

					return nativeSortFunction(
						a.name,
						b.name,
						DataSourceSortDirection.Ascending
					);
				}
			);

			return {
				hasData: true,
				title: announcement.title,
				media: convertMedia(announcement.media),
				authorId: announcement.authorId,
				authorName: announcement.authorName,
				areCommentsEnabled: announcement.areCommentsEnabled,
				categories: sortedCategories,
				selectedCategoryId,
				isEditable: announcement.isEditable,
				isPinned: isNotNil(announcement.pinDate),
				layout: announcement.layout,
				publicationDate: announcement.publicationStart,
				tags: announcement.tags
					.map(convertTagForDetails),
			};
		},
		[
			announcement,
			selectedCategoryId,
			getCategory
		]
	);

	const announcementContentModeProps = useMemo(
		(): AnnouncementContentModeProps => {
			switch (mode) {
				case "details-only":
					return {
						mode: "details-only",
						onSelectCategory: (categoryId: number): void => {
							guardAgainstUnsavedComment(
								() => onSelectCategory(categoryId)
							);
						}
					};
				case "full":
					return {
						mode: "full",
						activeFilter: activeFilter,
						onBack: onBack,
						onEdit: editHandler,
						onSelectCategory: (categoryId: number): void => {
							guardAgainstUnsavedComment(
								() => onSelectCategory(categoryId)
							);
						},
						onSelectAuthor: (authorId: number, authorName: string): void => {
							guardAgainstUnsavedComment(
								() => onActiveFilterChange({
									kind: "author",
									value: {
										id: authorId,
										name: authorName
									}
								})
							);
						},
						onTagClick: (id: number | null): void => {
							const tag: TagViewModel = ensureNotNil(
								announcement?.tags
									.find(x => x.id === id)
							);

							onActiveFilterChange({
								kind: "tag",
								value: {
									...tag,
									itemKind: HubItemKind.Announcement
								}
							});
						}
					};
				default:
					never(mode);
			}
		},
		[
			activeFilter,
			announcement?.tags,
			editHandler,
			guardAgainstUnsavedComment,
			mode,
			onActiveFilterChange,
			onBack,
			onSelectCategory
		]
	);

	const isAllowedToLookUpViewers: boolean = isNotNil(announcement)
		&& announcement.isEditable;

	const announcementContentStatisticsProps = useMemo(
		(): AnnouncementContentStatisticsProps => {
			if (!statistics) {
				return {
					hasStatistics: false
				};
			}

			return {
				hasStatistics: true,
				statistics: statistics,
				onStatisticsRetrieved: setStatistics,
				onOpenReactionsDialog: () => openReactionsDialog({
					itemId: announcementId,
					getReactions: getReactions,
					getReactionCounts: getReactionCounts
				}),
				onOpenViewersDialog: isAllowedToLookUpViewers
					? () => openViewersDialog({
						count: statistics.viewsCount,
						itemId: announcementId,
						itemKind: HubItemKind.Announcement
					})
					: undefined
			};
		},
		[
			announcementId,
			isAllowedToLookUpViewers,
			openReactionsDialog,
			openViewersDialog,
			statistics
		]
	);

	const announcementCommentService = useMemo(
		(): HubCommentSectionService => {
			return getAnnouncementCommentService(announcementId);
		},
		[announcementId]
	);

	const getRelatedCommentThreadHandler = useCallback(
		(commentId: number, signal?: AbortSignal): Promise<readonly HubCommentModel[]> => {
			return getRelatedCommentThread(
				announcementId,
				commentId,
				signal
			);
		},
		[announcementId]
	);

	const commentsCount: number = statistics?.commentsCount
		.isSet
		? statistics.commentsCount
			.value
		: 0;

	const renderCommentsSection = (): ReactElement | null => {
		if (!announcement) {
			return null;
		}

		if (!announcement.areCommentsEnabled) {
			return (
				<HubCommentSection.NoComments />
			);
		}

		return isNotNil(commentOptions)
			? (
				<div className={styles.commentsSectionContainer}>
					{commentsState.kind === "comment-thread"
						? (
							<CommentThread
								commentId={commentsState.commentId}
								onStatisticsUpdate={reloadStatistics}
								onMobileCommentIsEditingChanged={setIsEditingMobileComment}
								options={commentOptions}
								timezone={userTimezone}
								onAllCommentView={allCommentViewHandler}
								allCommentsCount={commentsCount}
								getRelatedCommentThread={getRelatedCommentThreadHandler}
								service={announcementCommentService}
							/>
						)
						: (
							<div
								className={
									commentsState.shouldReserveSpace
										? styles.withMinHeight
										: undefined
								}
							>
								<HubItemCommentSection
									kind={HubItemKind.Announcement}
									onStatisticsUpdate={reloadStatistics}
									onMobileCommentIsEditingChanged={setIsEditingMobileComment}
									timezone={userTimezone}
									options={commentOptions}
									allCommentsCount={commentsCount}
									service={announcementCommentService}
								/>
							</div>
						)
					}
				</div>
			)
			: null;
	};

	return (
		<>
			<ContentWithRelevant trackingId="announcement-details">
				<ContentWithRelevant.Content hasBusyIndicator={loadingStatus === "loading"}>
					<AnnouncementContent
						{...announcementContentDataProps}
						{...announcementContentModeProps}
						{...announcementContentStatisticsProps}
						timezone={userTimezone}
						onViewImage={viewImageHandler}
						linkedDocumentsMode="view"
						linkedDocuments={linkedDocuments}
						onChangeIsLoading={isLoading => setLoadingStatus(isLoading ? "loading" : "found")}
					/>

					{renderCommentsSection()}

					{loadingStatus === "not-found" && (
						<NoResults
							{...(mode === "full"
								? {
									retryText: localize("_BackToOverview"),
									onRetry: onBack
								}
								: {}
							)}
							icon="bullhorn"
							title={localize("_ItemNotFound")}
							message={localize("_TheItemMayHaveBeenDeletedOrYouDontHaveThePermissionsToReadIt")}
							trackingId="announcement-not-found"
						/>
					)}
				</ContentWithRelevant.Content>

				{(mode === "full" && loadingStatus !== "not-found") && (
					<ContentWithRelevant.Relevant title={localize("_OtherAnnouncements")}>
						<RelevantAnnouncementsSection
							item={announcement}
							viewsCount={statistics?.viewsCount}
							activeFilter={activeFilter}
							selectedCategoryId={selectedCategoryId}
							searchTerm={searchTerm}
							sorting={sorting}
							onNavigateToItem={onNavigateToItem}
							timezone={userTimezone}
							isNewFilter={isNewFilter}
						/>
					</ContentWithRelevant.Relevant>
				)}
				{announcement?.areCommentsEnabled && (
					<MobileCommentBoxPreservedSpace />
				)}
				{viewLocation === HubItemViewLocation.Hub && (
					<AiAssistantFabSpacing />
				)}
			</ContentWithRelevant>

			{confirmationDialogElement}
			{imageViewerDialog}

			{isNotNil(idToEdit) && isNotNil(closeEditDialogHandler) && (
				<EditDialog
					mode={{
						kind: "edit",
						announcementId: idToEdit
					}}
					onSave={afterSaveHandler}
					onClose={closeEditDialogHandler}
					onBack={closeEditDialogHandler}
				/>
			)}
			{reactionsDialog}
			{viewersDialog}
		</>
	);
};

Details.displayName = "Details";

type LoadingStatus =
	| "loading"
	| "found"
	| "not-found"
	| "error";


export type AnnouncementDetailsDeepLink = {
	kind: "comment";
	commentId: number;
} | {
	kind: "comments";
};

type CommentsState = {
	kind: "all-comments";
	shouldReserveSpace?: boolean;
} | {
	kind: "comment-thread";
	commentId: number;
};

function convertMedia(media: HubItemMediaDetailsViewModel): MediaContentModel {
	switch(media.mediaKind) {
		case HubItemMediaKind.Image:
			return {
				mediaKind: HubItemMediaKind.Image,
				imageUrl: getHubImageUrl(HubDataSource.Announcement, media.imageId),
				attribution: media.attribution
			};
		default:
			return media;
	}
}
